// src/components/onboarding/StepMode.js
import React from 'react';
import { Users, Heart } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { calculateAge } from '../../utils';

const MODES = [
  { value: 'community', icon: Users, colorClass: 'from-indigo-500 to-blue-600' },
  { value: 'dating', icon: Heart, colorClass: 'from-rose-500 to-fuchsia-600', adultOnly: true },
  {
    value: 'both',
    icon: ({ size, ...props }) => (
      <span className="flex items-center gap-1" {...props}>
        <Users size={size - 4} />
        <Heart size={size - 4} />
      </span>
    ),
    colorClass: 'from-purple-500 to-rose-500',
    adultOnly: true,
  },
];

const StepMode = ({ data, onChange, errors }) => {
  const { t } = useTranslation();
  const age = calculateAge(data.dateOfBirth);
  const isMinor = age !== null && age < 18;

  const availableModes = isMinor ? MODES.filter((m) => !m.adultOnly) : MODES;

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-lg sm:text-xl font-bold dark:text-gray-100">
          {t('onboarding.stepMode.title')}
        </h2>
        <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
          {t('onboarding.stepMode.subtitle')}
        </p>
      </div>

      <div className="space-y-3">
        {availableModes.map((mode) => {
          const Icon = mode.icon;
          const selected = data.mode === mode.value;
          return (
            <button
              key={mode.value}
              type="button"
              data-testid={`mode-card-${mode.value}`}
              onClick={() => onChange({ mode: mode.value })}
              className={`w-full flex items-center gap-3 sm:gap-4 p-4 rounded-xl border-2 transition-all text-left ${
                selected
                  ? 'border-rose-500 bg-rose-50 dark:bg-rose-950/30 shadow-md'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <div
                className={`w-10 h-10 rounded-full bg-gradient-to-br ${mode.colorClass} flex items-center justify-center text-white flex-shrink-0`}
              >
                <Icon size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className={`text-sm font-semibold ${selected ? 'text-rose-600 dark:text-rose-400' : 'text-gray-800 dark:text-gray-200'}`}
                >
                  {t(`onboarding.stepMode.${mode.value}Title`)}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {t(`onboarding.stepMode.${mode.value}Desc`)}
                </p>
              </div>
              <div
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  selected ? 'border-rose-500 bg-rose-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              >
                {selected && <div className="w-2 h-2 rounded-full bg-white" />}
              </div>
            </button>
          );
        })}
      </div>

      {isMinor && (
        <p
          data-testid="mode-minor-notice"
          className="text-sm text-gray-500 dark:text-gray-400 text-center italic"
        >
          {t('onboarding.stepMode.minorNotice')}
        </p>
      )}

      {errors.mode && (
        <p data-testid="mode-error" className="text-red-500 text-sm text-center">
          {errors.mode}
        </p>
      )}
    </div>
  );
};

export default StepMode;
